import { PlaceOutlined, TagOutlined, WarehouseOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  GlobalStyles,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import DirectUploadField from "components/Form/DirectUploadField";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import TechnicianInventoryImages from "components/TechnicianInventory/TechnicianInventoryImages";
import { InventoryDeviceWipeChip } from "components/TechnicianInventory/v2/InventoryDeviceWipeStatus";
import { colors } from "constants/colors";
import { conditions } from "constants/conditions";
import { deviceTypes } from "constants/device_types";
import { FormikProvider, useFormik } from "formik";
import { useAlert } from "hooks/useAlert";
import { AutoPagePaperLayout } from "pages/Layouts/AutoPagePaperLayout";
import { MetaList, MetaListItem } from "pages/Layouts/components/MetaList";
import { BreadcrumbLink, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Waypoint } from "react-waypoint";
import { defaultInventory, Inventory, KeyboardLanguage } from "types/inventory";
import { RecordHistoryEvent } from "types/recordHistoryEvent";
import { Tag } from "types/tag";
import { inventoryEditValidation } from "validations/inventory";
import ConditionSuggestion from "./ConditionSuggestion";
import { SerialVerificationNotice } from "./SerialVerificationNotice";
import { useClearSerialVerification } from "./useClearSerialVerification";
import { InventoryStateMetaListItem } from "./InventoryStateMetaListItem";
import { ResaleSection } from "./ResaleSection";
import Tags from "./Tags";

type InventoryEditProps = {
  inventory?: Inventory;
  inventoryEvents?: RecordHistoryEvent[];
  loadNextInventoryEvents: () => void;
  isLoadingInventoryEvents: boolean;
  updateInventory: (props: Inventory) => Promise<AxiosResponse<Inventory>>;
};

const olderEventsURL =
  "https://app.hex.tech/d8ff35c6-9bfe-4f6c-b30b-a4d56f722e73/app/363004fc-0433-4e5e-9f11-ba83bc4a82f0/latest";

export default function InventoryEdit({
  inventory,
  updateInventory,
  inventoryEvents,
  loadNextInventoryEvents,
  isLoadingInventoryEvents,
}: InventoryEditProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formValues = useMemo(() => {
    return inventory || defaultInventory;
  }, [inventory]);
  const { alertError } = useAlert();
  const [serialCheckCleared, setSerialCheckCleared] = useState(false);
  const { clear: confirmSerial, confirming: confirmingSerial } = useClearSerialVerification(inventory?.id, () => {
    setSerialCheckCleared(true);
  });

  useEffect(() => {
    setSerialCheckCleared(false);
  }, [inventory?.id]);
  const formik = useFormik<Inventory>({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: formValues,
    validationSchema: inventoryEditValidation,
    onSubmit: async (values) => {
      try {
        await updateInventory(values);
        navigate(-1);
      } catch {
        alertError("There was an error, please try again");
      }
    },
  });
  const yearsArray = () => {
    const max = new Date().getFullYear();
    const min = max - 20;
    const years = [];

    for (let i = max; i >= min; i--) {
      years.push(i);
    }
    return years;
  };

  const historyRow = (content: ReactElement | string, key: number) => {
    return (
      <React.Fragment key={key}>
        <ListItem alignItems="flex-start">
          <ListItemText>{content}</ListItemText>
        </ListItem>
        <Divider component="li" />
      </React.Fragment>
    );
  };

  const crumbs: BreadcrumbLink[] = [{ label: "Warehouse" }];
  if (searchParams.get("via") == "inventories") {
    crumbs.push({ label: "Inventories", path: "/inventories" });
  } else {
    crumbs.push(
      { label: "Pickups", path: "/pickups" },
      {
        label: `Pickup ${inventory?.pickup.id}`,
        path: `/pickups/${inventory?.pickup.id}`,
      },
    );
  }

  return (
    <AutoPagePaperLayout paperProps={{ sx: { padding: 3 } }}>
      <PageBreadcrumbs links={crumbs} />
      <PageTitle>Inventory {inventory?.label} </PageTitle>
      <SerialVerificationNotice
        required={Boolean(inventory?.serialVerificationRequired) && !serialCheckCleared}
        confirming={confirmingSerial}
        onConfirm={() => {
          void confirmSerial();
        }}
      />
      <PageSubtitle>
        <MetaList>
          <MetaListItem
            title="ID"
            label={
              <Link component={RouterLink} to={`/technician/${inventory?.id}`}>
                {inventory?.id}
              </Link>
            }
            icon={<TagOutlined />}
          />
          <InventoryStateMetaListItem state={inventory?.state} />
          <MetaListItem title="Warehouse" label={inventory?.warehouse?.name} icon={<WarehouseOutlined />} />
          <MetaListItem
            title="Warehouse Location"
            label={<WarehouseLocationLink code={inventory?.warehouseLocation} locationId={inventory?.locationId} />}
            icon={<PlaceOutlined />}
          />
          <MetaListItem
            title="Dynamic Inventory Group"
            label={inventory?.dynamicInventoryGroup}
            icon={<PlaceOutlined />}
          />
          <InventoryDeviceWipeChip inventory={inventory} size={"small"} />
        </MetaList>
      </PageSubtitle>
      <FormikProvider value={formik}>
        <GlobalStyles
          styles={{
            html: { viewTransitionName: "none" },
            "html::view-transition-group(.card)": {
              animationTimingFunction: "ease-in-out",
              animationDuration: "0.5s",
            },
          }}
        />
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            ".css-k0xjq9-MuiTypography-root": { pt: 1 },
            ".MuiChip-deleteIcon": { color: "#ffffff" },
          }}
          noValidate
          autoComplete="off"
        >
          <Grid container sm={12} spacing={2}>
            <Grid item lg={6}>
              <Grid container direction="row" spacing={1}>
                <Grid item sm={6}>
                  <Typography variant="overline">Manufacturer</Typography>
                  <TextField
                    id="manufacturer"
                    name="manufacturer"
                    size="small"
                    fullWidth
                    value={formik.values.manufacturer || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Device Type</Typography>
                  <Select
                    id="deviceType"
                    name="deviceType"
                    size="small"
                    fullWidth
                    value={formik.values.deviceType || ""}
                    onChange={formik.handleChange}
                  >
                    {deviceTypes.map((device) => {
                      return (
                        <MenuItem key={device} value={device}>
                          {device}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Serial Number</Typography>
                  <TextField
                    id="serialNumber"
                    name="serialNumber"
                    size="small"
                    fullWidth
                    value={formik.values.serialNumber || ""}
                    onChange={formik.handleChange}
                    helperText={formik.errors.serialNumber}
                    error={Boolean(formik.errors.serialNumber)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> SKU</Typography>
                  <TextField
                    id="sku"
                    name="sku"
                    size="small"
                    fullWidth
                    value={formik.values.sku || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Model</Typography>
                  <TextField
                    id="model"
                    name="model"
                    size="small"
                    fullWidth
                    value={formik.values.model || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Model Order</Typography>
                  <TextField
                    id="modelOrder"
                    name="modelOrder"
                    size="small"
                    multiline
                    fullWidth
                    value={formik.values.modelOrder || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Year</Typography>
                  <Select
                    id="year"
                    name="year"
                    size="small"
                    fullWidth
                    value={formik.values.year || ""}
                    onChange={formik.handleChange}
                  >
                    {yearsArray().map((year: number) => {
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Cosmetic Grade</Typography>
                  <Select
                    id="condition"
                    name="condition"
                    size="small"
                    fullWidth
                    value={formik.values.condition || ""}
                    onChange={formik.handleChange}
                  >
                    {conditions.map((condition) => {
                      return (
                        <MenuItem key={condition} value={condition}>
                          {condition}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Security Level</Typography>
                  <Select
                    id="securityLevel"
                    name="securityLevel"
                    size="small"
                    fullWidth
                    value={formik.values.securityLevel || ""}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value={"Not locked"}>Not locked</MenuItem>
                    <MenuItem value={"EFI locked"}>EFI locked</MenuItem>
                    <MenuItem value={"BIOS locked"}>BIOS locked</MenuItem>
                    <MenuItem value={"Elevated security"}>Elevated security</MenuItem>
                    <MenuItem value={"Icloud locked"}>Icloud locked</MenuItem>
                    <MenuItem value={"Activation lock"}>Activation lock</MenuItem>
                  </Select>
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Battery Count</Typography>
                  <TextField
                    id="batteryCount"
                    name="batteryCount"
                    size="small"
                    fullWidth
                    value={formik.values.batteryCount || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Color</Typography>
                  <Select
                    id="color"
                    name="color"
                    size="small"
                    fullWidth
                    value={formik.values.color || ""}
                    onChange={formik.handleChange}
                  >
                    {colors.map((color) => (
                      <MenuItem key={color} value={color}>
                        {color}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item sm={6}>
                  <Typography variant="overline"> Asset Tag</Typography>
                  <TextField
                    id="asset_tag"
                    name="assetTag"
                    size="small"
                    fullWidth
                    value={formik.values.assetTag || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item sm={12}>
                  <Typography variant="overline"> Enrolled</Typography>
                  <Select
                    displayEmpty
                    id="enrolledStatus"
                    name="enrolledStatus"
                    size="small"
                    fullWidth
                    value={formik.values.enrolledStatus || ""}
                    onChange={formik.handleChange}
                  >
                    {[
                      ["enrolled", "Enrolled"],
                      ["unenrolled", "Unenrolled"],
                      ["api_unenrolled", "API Unenrolled"],
                      ["pending_revivn_unenrollment_confirmation", "Pending Revivn Unenrollment Confirmation"],
                      ["not_found", "Not Found"],
                    ].map((status) => {
                      return (
                        <MenuItem key={status[0]} value={status[0]}>
                          {status[1]}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    <span>Last enrollment check at: </span>
                    <span>
                      {inventory?.lastEnrollmentCheckAt ? (
                        `${Intl.DateTimeFormat("en-US", {
                          timeZone: "America/New_York",
                          dateStyle: "short",
                          timeStyle: "long",
                        }).format(Date.parse(inventory.lastEnrollmentCheckAt))}`
                      ) : (
                        <Box sx={{ fontStyle: "italic", display: "inline" }}>not checked yet</Box>
                      )}
                    </span>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline"> Enrolled Organization</Typography>
                  <TextField
                    id="enrolledOrganization"
                    name="enrolledOrganization"
                    size="small"
                    fullWidth
                    value={formik.values.enrolledOrganization || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline"> RAM</Typography>
                  <TextField
                    id="ram"
                    name="ram"
                    size="small"
                    fullWidth
                    value={formik.values.ram || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline"> CPU</Typography>
                  <TextField
                    id="cpu"
                    name="cpu"
                    size="small"
                    fullWidth
                    value={formik.values.cpu || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline"> GPU</Typography>
                  <TextField
                    id="gpu"
                    name="gpu"
                    size="small"
                    multiline
                    fullWidth
                    value={formik.values.gpu || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline"> Hard Drive</Typography>
                  <TextField
                    id="hardDrive"
                    name="hardDrive"
                    size="small"
                    fullWidth
                    value={formik.values.hardDrive || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline">Drive Size (GB)</Typography>
                  <TextField
                    id="hardDriveSizeInGb"
                    name="hardDriveSizeInGb"
                    size="small"
                    fullWidth
                    value={formik.values.hardDriveSizeInGb || ""}
                    onChange={formik.handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
              <Typography variant="overline" sx={{ pt: 2 }}>
                Description
              </Typography>
              <TextField
                id="description"
                name="description"
                size="small"
                multiline={true}
                fullWidth
                value={formik.values.description || ""}
                onChange={formik.handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Grid item sm={12}>
                <Typography variant="overline">Keyboard Language</Typography>
                <Select
                  displayEmpty
                  id="keyboardLanguage"
                  name="keyboardLanguage"
                  size="small"
                  fullWidth
                  value={formik.values.keyboardLanguage ? formik.values.keyboardLanguage : null}
                  onChange={formik.handleChange}
                >
                  {Object.keys(KeyboardLanguage).map((languageCode) => {
                    return (
                      <MenuItem key={languageCode} value={languageCode}>
                        {KeyboardLanguage[languageCode as keyof typeof KeyboardLanguage]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
              <Typography variant="overline" sx={{ pt: 2 }}>
                Comments
              </Typography>
              <TextField
                id="comments"
                name="comments"
                multiline={true}
                maxRows={6}
                fullWidth
                value={formik.values.comments || ""}
                onChange={formik.handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography variant="overline" sx={{ pt: 2 }}>
                Tags
              </Typography>
              <Tags
                tags={formik.values.tags || []}
                onChange={(tags: Tag[]) => {
                  formik.setFieldValue("tags", tags);
                }}
              />
              <Typography variant="overline" sx={{ pt: 2 }}>
                Internal Photos
              </Typography>
              <Typography paragraph variant="caption" color="text.secondary">
                Photos from inbound (serial number & asset tag), photos from returns, etc.
              </Typography>
              <Box>{inventory ? <TechnicianInventoryImages /> : null}</Box>
              <Box marginY={4}>
                <Typography variant="overline">Resale Photos</Typography>
                <Typography paragraph variant="caption" color="text.secondary">
                  Photos uploaded here will be tagged as Resale and will appear externally on our ecom listings. They
                  should be high quality images revealing no sensitive device or client data. The first uploaded resale
                  photo should be a photo of the device with the screen open, facing directly towards the camera with
                  the entire screen and keyboard visible.
                </Typography>
                <Box>
                  <DirectUploadField name="resalePhotos" service="amazon_public" accept={["image/jpeg"]} multiple />
                </Box>
                {inventory?.metadata?.grade && inventory.metadata?.grade !== inventory.condition && (
                  <ConditionSuggestion inventoryMetadata={inventory.metadata} />
                )}
              </Box>
            </Grid>
            <Grid item lg={6}>
              <Typography variant="h6" sx={{ color: "#0089BD" }}>
                History
              </Typography>
              <Paper variant="outlined" sx={{ mt: 1 }}>
                <List
                  sx={{
                    width: "100%",
                    height: "700px",
                    overflow: "auto",
                  }}
                >
                  {inventoryEvents && inventoryEvents.map((event, i) => historyRow(event.description, i))}
                  {historyRow(
                    <>
                      For inventory events older than 6 months check{" "}
                      <RouterLink key={-1} to={olderEventsURL} target="_blank">
                        Older Events
                      </RouterLink>
                    </>,
                    -1,
                  )}
                  {!isLoadingInventoryEvents && <Waypoint onEnter={loadNextInventoryEvents} />}
                </List>
              </Paper>
              <Divider sx={{ my: 2 }} />
              {inventory && <ResaleSection inventory={inventory} />}
            </Grid>
            <Grid container sx={{ justifyContent: "center" }}>
              <Button
                color="primary"
                size="large"
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{ margin: "10px", width: "120px" }}
              >
                BACK
              </Button>
              <Button
                color="primary"
                size="large"
                variant="contained"
                type="submit"
                sx={{ margin: "10px", width: "120px" }}
                disabled={!formik.isValid}
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </Box>
      </FormikProvider>
    </AutoPagePaperLayout>
  );
}
